import { Company } from '../models/company-profile.model';

export interface CompanyMetrics {
  clients: number | null;
  users: number | null;
  candidates: number | null;
  activeJobs: number | null;
}

export interface CompanyReadinessItem {
  key: string;
  label: string;
  complete: boolean;
  unavailable?: boolean;
}

export const emptyCompanyMetrics: CompanyMetrics = {
  clients: null,
  users: null,
  candidates: null,
  activeJobs: null,
};

export const companyDescriptionText = (html: string): string => {
  if (!html) return '';

  if (typeof document === 'undefined') {
    return html
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  const element = document.createElement('div');
  element.innerHTML = html;
  return (element.textContent ?? '').replace(/\s+/g, ' ').trim();
};

export const companyWebsiteUrl = (website: string): string => {
  const value = website.trim();
  if (!value) return '';
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

export const companyWebsiteLabel = (website: string): string =>
  website
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');

export const getCompanyReadiness = (
  company: Company,
  hasLogo: boolean,
  metrics: CompanyMetrics
): CompanyReadinessItem[] => [
  {
    key: 'basics',
    label: 'Company name and website',
    complete: Boolean(company.name.trim() && company.website.trim()),
  },
  {
    key: 'story',
    label: 'Company story added',
    complete: Boolean(companyDescriptionText(company.aboutUs)),
  },
  {
    key: 'logo',
    label: 'Company logo uploaded',
    complete: hasLogo,
  },
  {
    key: 'team',
    label: 'Team member added',
    complete: (metrics.users ?? 0) > 0,
    unavailable: metrics.users === null,
  },
  {
    key: 'client',
    label: 'First client configured',
    complete: (metrics.clients ?? 0) > 0,
    unavailable: metrics.clients === null,
  },
];

export const getCompanyCompletion = (
  company: Company,
  hasLogo: boolean,
  metrics: CompanyMetrics
): number => {
  const readiness = getCompanyReadiness(company, hasLogo, metrics);
  return Math.round(
    (readiness.filter((item) => item.complete).length / readiness.length) * 100
  );
};
